/**
 * Conversion-Focused Breadcrumb Navigation
 * Carbon Design System compliant breadcrumb for marketing conversion flow
 * Navigation Flow: Landing → Features → Pricing → Demo Request → Trial Signup
 */

import React from 'react';
import { Breadcrumb, BreadcrumbItem, ProgressIndicator, ProgressStep } from '@carbon/react';
import { CheckmarkFilled, InProgress } from '@carbon/icons-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MarketingVariant } from '../../routes/MarketingRouter';

interface ConversionBreadcrumbProps {
  variant?: MarketingVariant;
  className?: string;
}

interface ConversionStep {
  path: string;
  label: string;
  shortLabel: string;
  description: string;
  conversionAction?: string;
}

const ConversionBreadcrumb: React.FC<ConversionBreadcrumbProps> = ({ variant = 'default', className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Define the conversion funnel steps per the work item requirements
  const conversionSteps: ConversionStep[] = [
    {
      path: '/',
      label: 'Discover Sigma08',
      shortLabel: 'Home',
      description: 'Learn about AI-powered education',
      conversionAction: 'initial_interest',
    },
    {
      path: '/features',
      label: 'Explore Features',
      shortLabel: 'Features',
      description: 'See what makes us different',
      conversionAction: 'feature_exploration',
    },
    {
      path: '/pricing',
      label: 'View Pricing',
      shortLabel: 'Pricing',
      description: 'Find the right plan',
      conversionAction: 'pricing_consideration',
    },
    {
      path: '/demo',
      label: 'Request Demo',
      shortLabel: 'Demo',
      description: 'See it in action',
      conversionAction: 'demo_request',
    },
    {
      path: '/trial',
      label: 'Start Free Trial',
      shortLabel: 'Trial',
      description: 'Begin your journey',
      conversionAction: 'trial_signup',
    },
  ];

  // Get current step index based on current path
  const getCurrentStepIndex = (): number => {
    const currentPath = location.pathname;
    const stepIndex = conversionSteps.findIndex(step => step.path === currentPath);
    return stepIndex >= 0 ? stepIndex : 0;
  };

  const currentStepIndex = getCurrentStepIndex();

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (path: string, stepIndex: number) => {
    // Track conversion funnel navigation
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'conversion_navigation', {
        event_category: 'Marketing',
        event_label: `Step ${stepIndex + 1}: ${conversionSteps[stepIndex].shortLabel}`,
        value: stepIndex + 1,
        custom_parameters: {
          variant: variant,
          from_step: currentStepIndex,
          to_step: stepIndex,
          conversion_action: conversionSteps[stepIndex].conversionAction,
        },
      });
    }

    navigate(path);
  };

  // Get conversion progress percentage
  const getProgressPercentage = (): number => {
    return Math.round(((currentStepIndex + 1) / conversionSteps.length) * 100);
  };

  // Don't show breadcrumb on homepage per Carbon Design System
  if (location.pathname === '/') {
    return (
      <div className={`conversion-breadcrumb ${className}`}>
        {/* Progress Indicator for conversion funnel */}
        <div className="conversion-breadcrumb__progress">
          <ProgressIndicator currentIndex={currentStepIndex} spaceEqually>
            {conversionSteps.map((step, index) => (
              <ProgressStep
                key={step.path}
                complete={index < currentStepIndex}
                current={index === currentStepIndex}
                description={step.description}
                label={step.shortLabel}
                onClick={() => {
                  // Allow navigation to completed steps or current step
                  if (index <= currentStepIndex) {
                    handleBreadcrumbClick(step.path, index);
                  }
                }}
              />
            ))}
          </ProgressIndicator>

          {/* Progress percentage */}
          <div className="conversion-progress-stats">
            <span className="progress-text">
              Step {currentStepIndex + 1} of {conversionSteps.length}
            </span>
            <span className="progress-percentage">{getProgressPercentage()}% Complete</span>
          </div>
        </div>

        {/* Next step suggestion */}
        {currentStepIndex < conversionSteps.length - 1 && (
          <div className="conversion-next-step">
            <span className="next-step-text">Next: {conversionSteps[currentStepIndex + 1].label}</span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`conversion-breadcrumb ${className}`}>
      {/* Carbon Breadcrumb for navigation - not shown on homepage */}
      <Breadcrumb noTrailingSlash className="conversion-breadcrumb__nav">
        {conversionSteps.slice(0, currentStepIndex + 1).map((step, index) => (
          <BreadcrumbItem
            key={step.path}
            href={step.path}
            onClick={e => {
              e.preventDefault();
              handleBreadcrumbClick(step.path, index);
            }}
            isCurrentPage={index === currentStepIndex}
          >
            {step.shortLabel}
          </BreadcrumbItem>
        ))}
      </Breadcrumb>

      {/* Progress Indicator for conversion funnel */}
      <div className="conversion-breadcrumb__progress">
        <ProgressIndicator currentIndex={currentStepIndex} spaceEqually>
          {conversionSteps.map((step, index) => (
            <ProgressStep
              key={step.path}
              complete={index < currentStepIndex}
              current={index === currentStepIndex}
              description={step.description}
              label={step.shortLabel}
              onClick={() => {
                // Allow navigation to completed steps or current step
                if (index <= currentStepIndex) {
                  handleBreadcrumbClick(step.path, index);
                }
              }}
            />
          ))}
        </ProgressIndicator>

        {/* Progress percentage */}
        <div className="conversion-progress-stats">
          <span className="progress-text">
            Step {currentStepIndex + 1} of {conversionSteps.length}
          </span>
          <span className="progress-percentage">{getProgressPercentage()}% Complete</span>
        </div>
      </div>

      {/* Next step suggestion */}
      {currentStepIndex < conversionSteps.length - 1 && (
        <div className="conversion-next-step">
          <span className="next-step-text">Next: {conversionSteps[currentStepIndex + 1].label}</span>
        </div>
      )}
    </div>
  );
};

export default ConversionBreadcrumb;
